import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const BrainIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-6 h-6">
    <path d="M9.5 2a2.5 2.5 0 0 1 2.45 2H15a2 2 0 0 1 2 2v1.5a2.5 2.5 0 1 1 0 5V14a2 2 0 0 1-2 2h-1v1a3 3 0 0 1-6 0v-1H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h3.05A2.5 2.5 0 0 1 9.5 2z" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ChevronRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-6 h-6">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
  </svg>
);

const CheckCircleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6 text-lavender-400">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" strokeLinecap="round" strokeLinejoin="round"/>
    <polyline points="22 4 12 14.01 9 11.01" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-5 h-5">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
);

const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-6 h-6">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-6 h-6">
    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14" strokeLinecap="round"/>
  </svg>
);

const dataTypes = [
  { label: 'Account Information', detail: 'Name, email address, profile picture' },
  { label: 'App Content', detail: 'Mind maps, tasks, goals, plans you created' },
  { label: 'Usage Analytics', detail: 'App usage statistics and behavior data' },
  { label: 'Device Identifiers', detail: 'Push notification tokens, device IDs' },
  { label: 'Sync Data', detail: 'Cross-device synchronization records' },
  { label: 'Preferences', detail: 'App settings and personalization data' },
];

const steps = [
  {
    step: '01',
    icon: <MailIcon />,
    title: 'Submit Your Request',
    desc: 'Fill out the form below or send an email to our privacy team with your account email address.',
  },
  {
    step: '02',
    icon: <CheckCircleIcon />,
    title: 'Identity Verification',
    desc: 'We will send a verification email to confirm the deletion request is from the account owner.',
  },
  {
    step: '03',
    icon: <TrashIcon />,
    title: 'Data Permanently Deleted',
    desc: 'Once verified, all your data will be permanently and irreversibly deleted within 30 days.',
  },
];

export default function DataDeletion() {
  const [formData, setFormData] = useState({ email: '', reason: '', confirm: false });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Data Deletion Request – MindPlan AI';
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.email || !formData.confirm) return;
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
    }, 1500);
  };

  return (
    <div className="bg-neutral-950 text-white min-h-screen">
      {/* Header */}
      <div className="relative bg-hero-gradient border-b border-white/5 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-red-700/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-lavender-500/10 rounded-full blur-3xl" />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 relative z-10">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-white/40 text-sm mb-8">
            <Link to="/" className="hover:text-lavender-300 transition-colors">Home</Link>
            <ChevronRightIcon />
            <span className="text-lavender-300">Data Deletion</span>
          </div>

          <Link to="/" className="flex items-center gap-3 mb-8 group w-fit">
            <div className="w-10 h-10 rounded-xl bg-lavender-gradient flex items-center justify-center shadow-lavender">
              <BrainIcon />
            </div>
            <span className="font-display font-bold text-xl">MindPlan AI</span>
          </Link>

          <div className="inline-flex items-center gap-2 bg-red-600/20 border border-red-500/30 rounded-full px-4 py-1.5 mb-4">
            <span className="w-2 h-2 rounded-full bg-red-400" />
            <span className="text-red-300 text-xs font-medium tracking-wider uppercase">Account & Data Deletion</span>
          </div>

          <h1 className="font-display text-4xl md:text-5xl font-bold mb-4">
            Delete Your <span className="gradient-text">Account & Data</span>
          </h1>
          <p className="text-white/50 text-lg leading-relaxed max-w-2xl">
            We respect your right to control your personal data. This page lets you request permanent deletion of your MindPlan AI account and all associated data.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">

        {/* Warning box */}
        <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-5 mb-12 flex gap-4 items-start">
          <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
            <TrashIcon />
          </div>
          <div>
            <h3 className="font-semibold text-red-300 mb-1">This action is permanent and irreversible</h3>
            <p className="text-white/50 text-sm leading-relaxed">
              Once your data deletion request is confirmed and processed, all your MindPlan AI data — including plans, mind maps, tasks, and account details — will be permanently deleted. This cannot be undone.
            </p>
          </div>
        </div>

        {/* What will be deleted */}
        <section className="mb-14">
          <h2 className="font-display text-2xl font-bold mb-2">
            What Data Will Be <span className="gradient-text">Deleted</span>
          </h2>
          <p className="text-white/40 text-sm mb-6">All of the following data associated with your account will be permanently removed:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {dataTypes.map((item, idx) => (
              <div key={idx} className="glass-card px-5 py-4 flex items-start gap-3 hover:border-red-500/20 transition-colors duration-300">
                <div className="w-2 h-2 rounded-full bg-lavender-400 mt-2 flex-shrink-0" />
                <div>
                  <p className="font-medium text-white text-sm">{item.label}</p>
                  <p className="text-white/40 text-xs mt-0.5 leading-relaxed">{item.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Timeline + Process */}
        <section className="mb-14">
          <h2 className="font-display text-2xl font-bold mb-8">
            How the <span className="gradient-text">Process Works</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {steps.map((step, idx) => (
              <div key={idx} className="relative glass-card p-6 flex flex-col gap-4">
                {idx < steps.length - 1 && (
                  <div className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-10">
                    <ChevronRightIcon />
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-lavender-gradient flex items-center justify-center font-display font-bold text-sm shadow-lavender">
                    {step.step}
                  </div>
                </div>
                <div className="text-lavender-400">{step.icon}</div>
                <h3 className="font-semibold text-white">{step.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>

          {/* Timeline chips */}
          <div className="mt-8 flex flex-wrap gap-4">
            {[
              { icon: <ClockIcon />, label: 'Verification email within 1 hour' },
              { icon: <ClockIcon />, label: 'Data deleted within 30 days' },
              { icon: <ShieldIcon />, label: 'Backups purged within 90 days' },
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 glass-card px-4 py-2.5 text-sm text-white/60">
                <span className="text-lavender-400">{item.icon}</span>
                {item.label}
              </div>
            ))}
          </div>
        </section>

        {/* Alternatives */}
        <section className="mb-14 glass-card-light p-6 rounded-2xl">
          <h2 className="font-display text-xl font-bold mb-3 text-white">
            Before You Delete — Consider Alternatives
          </h2>
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            {[
              { title: 'Export Your Data', desc: 'Download all your plans and mind maps before deleting. Go to Settings > Account > Export Data.' },
              { title: 'Pause Your Account', desc: 'If you need a break, you can simply log out. Your data will be safely stored until you return.' },
              { title: 'Contact Support', desc: 'If you\'re having issues, our support team at support@mindplanai.com can often help resolve them.' },
              { title: 'Manage Privacy Settings', desc: 'You can adjust what data we collect and how we use it in Settings > Privacy.' },
            ].map((alt, idx) => (
              <div key={idx} className="flex gap-3">
                <CheckCircleIcon />
                <div>
                  <p className="font-semibold text-white text-sm">{alt.title}</p>
                  <p className="text-white/50 text-xs leading-relaxed mt-0.5">{alt.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Deletion Form */}
        <section className="mb-12">
          <h2 className="font-display text-2xl font-bold mb-2">
            Submit Your <span className="gradient-text">Deletion Request</span>
          </h2>
          <p className="text-white/40 text-sm mb-8">
            Fill out the form below or email us at{' '}
            <a href="mailto:privacy@mindplanai.com" className="text-lavender-400 hover:text-lavender-300 underline transition-colors">
              privacy@mindplanai.com
            </a>
          </p>

          {submitted ? (
            <div className="glass-card p-10 text-center">
              <div className="w-16 h-16 rounded-2xl bg-lavender-gradient flex items-center justify-center mx-auto mb-6 shadow-lavender-lg">
                <CheckCircleIcon />
              </div>
              <h3 className="font-display text-2xl font-bold text-white mb-3">Request Received</h3>
              <p className="text-white/50 leading-relaxed max-w-md mx-auto text-sm">
                We've received your data deletion request for <span className="text-lavender-300">{formData.email}</span>. You will receive a verification email within 1 hour. Please click the link in that email to confirm and initiate the deletion process.
              </p>
              <p className="text-white/30 text-xs mt-6">Reference ID: MPL-DEL-{Math.random().toString(36).substr(2, 9).toUpperCase()}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="glass-card p-8 flex flex-col gap-6">
              {/* Email */}
              <div className="flex flex-col gap-2">
                <label className="text-white/70 text-sm font-medium" htmlFor="del-email">
                  Account Email Address <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30">
                    <MailIcon />
                  </div>
                  <input
                    id="del-email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                    placeholder="you@example.com"
                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-12 pr-4 py-3.5 text-white placeholder-white/30 focus:outline-none focus:border-lavender-500 focus:ring-1 focus:ring-lavender-500 transition-colors duration-200 text-sm"
                  />
                </div>
              </div>

              {/* Reason (optional) */}
              <div className="flex flex-col gap-2">
                <label className="text-white/70 text-sm font-medium" htmlFor="del-reason">
                  Reason for Deletion <span className="text-white/30">(optional)</span>
                </label>
                <select
                  id="del-reason"
                  value={formData.reason}
                  onChange={e => setFormData({ ...formData, reason: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white focus:outline-none focus:border-lavender-500 focus:ring-1 focus:ring-lavender-500 transition-colors duration-200 text-sm"
                >
                  <option value="" className="bg-neutral-900">Select a reason</option>
                  <option value="no-longer-needed" className="bg-neutral-900">No longer need the app</option>
                  <option value="privacy-concerns" className="bg-neutral-900">Privacy concerns</option>
                  <option value="switching-app" className="bg-neutral-900">Switching to a different app</option>
                  <option value="too-many-issues" className="bg-neutral-900">Too many technical issues</option>
                  <option value="other" className="bg-neutral-900">Other</option>
                </select>
              </div>

              {/* Confirmation checkbox */}
              <label className="flex items-start gap-3 cursor-pointer group">
                <div className="relative mt-0.5">
                  <input
                    type="checkbox"
                    id="del-confirm"
                    required
                    checked={formData.confirm}
                    onChange={e => setFormData({ ...formData, confirm: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-5 h-5 rounded border-2 border-white/20 peer-checked:border-lavender-500 peer-checked:bg-lavender-600 transition-all duration-200 flex items-center justify-center group-hover:border-lavender-400">
                    {formData.confirm && (
                      <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" className="w-3 h-3">
                        <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                </div>
                <span className="text-white/50 text-sm leading-relaxed">
                  I understand that this action is <strong className="text-white">permanent and irreversible</strong>. All my data, including plans, mind maps, and account information, will be permanently deleted and cannot be recovered.
                </span>
              </label>

              {/* Submit */}
              <button
                type="submit"
                disabled={!formData.email || !formData.confirm || loading}
                className="w-full py-4 rounded-xl font-semibold text-sm transition-all duration-300 flex items-center justify-center gap-3
                  disabled:opacity-40 disabled:cursor-not-allowed
                  enabled:bg-gradient-to-r enabled:from-red-600 enabled:to-red-500 enabled:hover:from-red-500 enabled:hover:to-red-400
                  enabled:text-white enabled:shadow-lg enabled:hover:shadow-red-500/30 enabled:hover:scale-[1.02] enabled:active:scale-[0.98]"
              >
                {loading ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="32" strokeDashoffset="12"/>
                    </svg>
                    Processing Request...
                  </>
                ) : (
                  <>
                    <TrashIcon />
                    Submit Deletion Request
                  </>
                )}
              </button>

              <p className="text-white/30 text-xs text-center">
                By submitting, you agree that this request is final. A verification email will be sent to confirm.
              </p>
            </form>
          )}
        </section>

        {/* Alternative email method */}
        <div className="glass-card p-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex gap-4 items-center">
            <div className="w-10 h-10 rounded-xl bg-lavender-600/20 flex items-center justify-center text-lavender-400 flex-shrink-0">
              <MailIcon />
            </div>
            <div>
              <p className="font-semibold text-white text-sm">Prefer to email us directly?</p>
              <p className="text-white/40 text-xs mt-0.5">Send your request to our privacy team and we'll handle the rest.</p>
            </div>
          </div>
          <a
            href="mailto:privacy@mindplanai.com?subject=Data%20Deletion%20Request&body=Please%20delete%20my%20MindPlan%20AI%20account%20and%20all%20associated%20data.%0A%0AAccount%20Email%3A%20"
            className="btn-outline-lavender text-sm px-6 py-2.5 flex-shrink-0"
          >
            Email Privacy Team
          </a>
        </div>

        {/* Footer links */}
        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
          <p className="text-white/30 text-sm">
            &copy; {new Date().getFullYear()} MindPlan AI. All rights reserved.
          </p>
          <div className="flex gap-4">
            <Link to="/privacy-policy" className="text-lavender-400 text-sm hover:text-lavender-300 transition-colors">
              Privacy Policy
            </Link>
            <Link to="/" className="text-white/40 text-sm hover:text-lavender-300 transition-colors">
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
